import { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import { MapContainer, Marker, Polyline, TileLayer, ZoomControl, useMap, useMapEvents } from "react-leaflet";
import { pinIcon, clusterIcon, userIcon } from "./markers";
import { NORWAY_CENTER, DEFAULT_ZOOM, worstOf } from "@/lib/geo";
import { cn } from "@/lib/utils";

// Kartverket topografisk gråtone – roleg bakgrunn som lèt statusfargane dominere. © Kartverket (CC BY 4.0).
const TILE_URL = "https://cache.kartverket.no/v1/wmts/1.0.0/topograatone/default/webmercator/{z}/{y}/{x}.png";
const ATTRIBUTION = '&copy; <a href="https://www.kartverket.no" target="_blank" rel="noreferrer">Kartverket</a>';

const CELL_PX = 60;
const SAME_SPOT = 1e-4; // ~10 m

/** Grid-klynging i pikselrommet for gjeldande zoom. */
const buildClusters = (map, zoom, machines) => {
  const cells = new Map();
  for (const m of machines) {
    const p = map.project([m.position.lat, m.position.lng], zoom);
    const key = `${Math.floor(p.x / CELL_PX)}:${Math.floor(p.y / CELL_PX)}`;
    if (!cells.has(key)) cells.set(key, []);
    cells.get(key).push(m);
  }
  const out = [];
  for (const items of cells.values()) {
    const lat = items.reduce((s, m) => s + m.position.lat, 0) / items.length;
    const lng = items.reduce((s, m) => s + m.position.lng, 0) / items.length;
    const sameSpot = items.every((m) => Math.abs(m.position.lat - items[0].position.lat) < SAME_SPOT && Math.abs(m.position.lng - items[0].position.lng) < SAME_SPOT);
    out.push({ key: items.map((m) => m.id).sort().join("|"), lat, lng, items, sameSpot });
  }
  return out;
};

const ClusterLayer = ({ machines, selection, activeId, onPin, onCluster }) => {
  const map = useMap();
  const [tick, setTick] = useState(0);
  useMapEvents({ zoomend: () => setTick((t) => t + 1), moveend: () => setTick((t) => t + 1) });
  const zoom = map.getZoom();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const clusters = useMemo(() => buildClusters(map, zoom, machines), [map, zoom, machines, tick]);

  return clusters.map((c) => {
    if (c.items.length === 1) {
      const m = c.items[0];
      const sel = selection?.get(m.id);
      return (
        <Marker
          key={m.id}
          position={[m.position.lat, m.position.lng]}
          icon={pinIcon({ status: m.overall_status || "none", planned: m.is_planned, selected: !!sel, order: sel?.order, active: activeId === m.id })}
          zIndexOffset={activeId === m.id ? 1000 : sel ? 500 : 0}
          eventHandlers={{ click: () => onPin?.(m) }}
          title={m.label}
        />
      );
    }
    const status = worstOf(c.items.map((m) => m.overall_status)) || "none";
    const anySel = c.items.some((m) => selection?.has(m.id));
    return (
      <Marker
        key={c.key}
        position={[c.lat, c.lng]}
        icon={clusterIcon({ status, count: c.items.length, planned: c.items.some((m) => m.is_planned), selected: anySel })}
        zIndexOffset={anySel ? 500 : 0}
        eventHandlers={{
          click: () => {
            if (c.sameSpot || zoom >= 17) onCluster?.(c);
            else map.fitBounds(L.latLngBounds(c.items.map((m) => [m.position.lat, m.position.lng])), { padding: [60, 60], maxZoom: 17, animate: true });
          },
        }}
        title={`${c.items.length} maskiner`}
      />
    );
  });
};

/** Tilpass utsnittet første gong data kjem (og når `fitKey` endrar seg). */
const FitBounds = ({ points, fitKey }) => {
  const map = useMap();
  const done = useRef(null);
  useEffect(() => {
    if (!points.length || done.current === fitKey) return;
    done.current = fitKey;
    if (points.length === 1) map.setView(points[0], 13, { animate: false });
    else map.fitBounds(L.latLngBounds(points), { padding: [48, 48], maxZoom: 13, animate: false });
  }, [points, fitKey, map]);
  return null;
};

/** Gjer at kartet reknar om storleiken når containeren endrar seg (sidepanel, drawer). */
const InvalidateOnResize = () => {
  const map = useMap();
  useEffect(() => {
    const el = map.getContainer();
    const ro = new ResizeObserver(() => map.invalidateSize({ animate: false }));
    ro.observe(el);
    return () => ro.disconnect();
  }, [map]);
  return null;
};

/**
 * MachineMap – Leaflet-kart med statuspins, klynging, brukarposisjon og (valfri) rute-linje.
 * props:
 *   machines   : rader frå /api/geo/map (med position)
 *   selection  : Map<machine_id, {kind, order}> (turplanlegging)
 *   route      : [{lat,lng}] i rekkjefølgje (teiknar stipla linje)
 *   userPos    : {lat,lng} | null
 *   mapRef     : ref som får Leaflet-kartet
 */
export const MachineMap = ({ machines, selection, activeId, route, userPos, onPin, onCluster, mapRef, fitKey, className, children }) => {
  const points = useMemo(() => machines.map((m) => [m.position.lat, m.position.lng]), [machines]);
  return (
    <div className={cn("h-full w-full", className)} data-testid="machine-map">
      <MapContainer
        ref={mapRef}
        center={NORWAY_CENTER}
        zoom={DEFAULT_ZOOM}
        zoomControl={false}
        style={{ height: "100%", width: "100%" }}
        attributionControl
      >
        <TileLayer url={TILE_URL} attribution={ATTRIBUTION} maxZoom={18} />
        <ZoomControl position="bottomright" />
        <InvalidateOnResize />
        <FitBounds points={points} fitKey={fitKey} />
        {route && route.length > 1 && <Polyline positions={route.map((p) => [p.lat, p.lng])} pathOptions={{ className: "vms-route" }} />}
        {userPos && <Marker position={[userPos.lat, userPos.lng]} icon={userIcon()} zIndexOffset={2000} interactive={false} />}
        <ClusterLayer machines={machines} selection={selection} activeId={activeId} onPin={onPin} onCluster={onCluster} />
        {children}
      </MapContainer>
    </div>
  );
};
